import { useState } from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Link } from '~/components/link';
import { accreditationColorHex, AccreditationColorScheme, AccreditationFaq } from '~/features/accreditation/types';
import { trackEvent } from '~/features/analytics/track-event';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';
import { EyeIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export function AccreditationFaqsSection({
  colorScheme = 'blue',
  faqs,
  supportLink,
  formalDegreeKey,
}: {
  colorScheme?: AccreditationColorScheme;
  faqs: AccreditationFaq[];
  supportLink: string;
  formalDegreeKey?: FormalDegreeKey;
}) {
  const [showAll, setShowAll] = useState(false);

  const visibleFaqs = showAll ? faqs : faqs.slice(0, 10);

  function handleAccordionClick() {
    trackEvent({
      name: 'Masters Page CTA Clicked',
      type: 'click',
      label: 'faq_clicked',
      additionalAttributes: {
        formal_degree_key: formalDegreeKey,
      },
    });
  }

  function handleShowAllClick() {
    trackEvent({
      name: 'Masters Page CTA Clicked',
      type: 'click',
      label: showAll ? 'show_less_faqs_clicked' : 'show_all_faqs_clicked',
      additionalAttributes: {
        formal_degree_key: formalDegreeKey,
      },
    });

    setShowAll(!showAll);
  }

  return (
    <Flex as='section' flexDirection='column' gap={{ base: '56px', md: '72px' }}>
      <ContentContainer margin='0 auto' maxWidth='766px' width='100%'>
        <SectionHeader
          color='grey.500'
          descriptionSlot={
            <Text color='grey.500'>
              Get clear answers to the most common questions about the program, admissions, and accreditation.
            </Text>
          }
          titleSlot={
            <Heading as='h2' size={{ base: 'mobile-heading-2', md: 'heading-2' }}>
              Frequently Asked Questions
            </Heading>
          }
        />
      </ContentContainer>
      <ContentContainer
        as='section'
        contentMaxWidth='content-max-width-md'
        display='flex'
        flexDirection='column'
        gap='24px'
        id='faqs'
      >
        <Accordion
          variant='v2'
          sx={{
            '.chakra-accordion__item:has(.chakra-accordion__button[aria-expanded="true"])': {
              borderColor: `${colorScheme}.300 !important`,
            },
            '.chakra-accordion__button[aria-expanded="true"]': {
              bg: `${colorScheme}.50 !important`,
              borderBottomColor: `${colorScheme}.100 !important`,
              color: `${colorScheme}.500 !important`,
            },
            '.chakra-accordion__button:hover': {
              color: `${colorScheme}.500 !important`,
            },
          }}
          allowMultiple
        >
          {visibleFaqs.map((faq) => (
            <AccordionItem key={faq.question}>
              <h2>
                <AccordionButton onClick={handleAccordionClick}>
                  <Box as='span' flex='1' textAlign='left'>
                    {faq.question}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel>{faq.answer}</AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
        <Button
          alignSelf='center'
          borderColor='blue.700'
          boxShadow={`inset 0 0 0 1.5px ${accreditationColorHex.blue[700]}`}
          color='blue.700'
          fontWeight='600'
          gap='8px'
          variant='outline'
          width={{
            base: '100%',
            md: 'fit-content',
          }}
          onClick={handleShowAllClick}
        >
          <EyeIcon h='18px' w='18px' />
          {showAll ? 'Show less FAQs' : 'Show more FAQs'}
        </Button>
        <Link href={supportLink} mx='auto' isExternal>
          View all FAQs
        </Link>
      </ContentContainer>
    </Flex>
  );
}
